import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Archive, ListChecks, Loader2, Pencil, Plus, Send, Trash2, Users } from "lucide-react";
import api from "../../api/api";
import { Alert, Field, OrgModal, Panel, Pill, apiError, formatDate, useOrgStyles } from "../../components/org/orgUi";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";

/* Cohorts and the program's suggestion list (Alicia, Oct 2). A program repeats
   every term; a cohort is one run of it (Fall 2026). The program keeps a default
   list of suggestions; a cohort sends that list, or its own copy, to all of its
   students at once. Shown on a program's page; only org admins change things. */

const MAX_SUGGESTIONS = 20;

function dateRange(start, end) {
    if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
    if (start) return `From ${formatDate(start)}`;
    if (end) return `Until ${formatDate(end)}`;
    return "No dates";
}

function plural(n, word) {
    return `${n} ${word}${n === 1 ? "" : "s"}`;
}

const SENDING_OFF = "Sending isn't available yet: Build My Biz Brand still has to finish a database update.";

function PanelTitle({ icon: Icon, children }) {
    return <span className="inline-flex items-center gap-2"><Icon size={18} /> {children}</span>;
}

function SuggestionList({ items, empty }) {
    const s = useOrgStyles();
    if (!items.length) return <p className={`text-sm ${s.muted}`}>{empty}</p>;
    return (
        <ol className="space-y-2">
            {items.map((it, i) => (
                <li key={it.TemplateSuggestionID || i} className={`rounded-xl border p-3 ${s.subPanel}`}>
                    <div className={`text-sm font-semibold ${s.text}`}>{i + 1}. {it.Title}</div>
                    {it.Description && <p className={`mt-1 whitespace-pre-line text-sm ${s.body}`}>{it.Description}</p>}
                </li>
            ))}
        </ol>
    );
}

export default function ProgramCohorts({ orgId, programId, programName, isOrgAdmin }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [editingList, setEditingList] = useState(false);
    const [cohortForm, setCohortForm] = useState(null); // null | {} (new) | cohort
    const [openCohortId, setOpenCohortId] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/programs/${programId}/cohorts`);
            setData(res.data);
        } catch (err) {
            setError(apiError(err, "Couldn't load this program's cohorts."));
        }
    }, [orgId, programId]);

    useEffect(() => { load(); }, [load]);

    const saveProgramList = async (items) => {
        const res = await api.put(`/org/${orgId}/programs/${programId}/suggestions`, { suggestions: items });
        setData(res.data);
        setEditingList(false);
    };

    const archive = async (cohort) => {
        if (!window.confirm(`Archive "${cohort.CohortName}"? Its ${plural(cohort.StudentCount, "student")} go back to having no cohort. Suggestions already sent stay with them.`)) return;
        try {
            await api.delete(`/org/${orgId}/cohorts/${cohort.CohortID}`);
            await load();
        } catch (err) {
            setError(apiError(err));
        }
    };

    if (!data) {
        return (
            <>
                <Panel title={<PanelTitle icon={ListChecks}>Suggestions for this program</PanelTitle>}>
                    <Alert>{error}</Alert>
                    {!error && <InlineTableSkeleton isDarkMode={s.dark} rows={2} cols={1} bordered={false} />}
                </Panel>
                {!error && (
                    <Panel title={<PanelTitle icon={Users}>Cohorts</PanelTitle>}>
                        <InlineTableSkeleton isDarkMode={s.dark} rows={2} cols={4} bordered={false} />
                    </Panel>
                )}
            </>
        );
    }

    const { cohorts, suggestions, unassignedCount, canSend } = data;
    return (
        <>
            <Panel
                title={<PanelTitle icon={ListChecks}>Suggestions for this program</PanelTitle>}
                actions={isOrgAdmin && (
                    <button type="button" className={s.btnSecondary} onClick={() => setEditingList(true)}>
                        {suggestions.length ? <><Pencil size={14} /> Edit list</> : <><Plus size={14} /> Add suggestions</>}
                    </button>
                )}
            >
                <p className={`mb-4 text-sm ${s.muted}`}>
                    The list each cohort sends its students. Students find them in My Coaching → Suggestions, marked as
                    from {programName} and their cohort, and accept or decline each one.
                </p>
                <SuggestionList items={suggestions}
                                empty="No suggestions yet. Add what every student in this program should do, like updating their resume." />
            </Panel>

            <Panel
                title={<PanelTitle icon={Users}>Cohorts</PanelTitle>}
                actions={isOrgAdmin && (
                    <button type="button" className={s.btnPrimary} onClick={() => setCohortForm({})}><Plus size={14} /> New cohort</button>
                )}
            >
                <Alert onClose={() => setError("")}>{error}</Alert>
                <p className={`mb-4 text-sm ${s.muted}`}>
                    A cohort is one run of this program, like Fall 2026. Students who join the program go into the cohort
                    running that day, or the next one to start.
                </p>
                {isOrgAdmin && !canSend && (
                    <p className={`mb-4 rounded-lg border px-3 py-2 text-sm ${s.subPanel} ${s.body}`}>{SENDING_OFF}</p>
                )}
                {unassignedCount > 0 && cohorts.length > 0 && (
                    <p className={`mb-4 text-sm ${s.body}`}>
                        {plural(unassignedCount, "enrolled student")} {unassignedCount === 1 ? "isn't" : "aren't"} in a cohort yet.{isOrgAdmin ? " Open a cohort to add them." : ""}
                    </p>
                )}
                {cohorts.length === 0 ? (
                    <p className={`text-sm ${s.muted}`}>No cohorts yet.{isOrgAdmin ? " Use New cohort to add this term's group." : ""}</p>
                ) : (
                    <div className="relative overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                            <thead>
                                <tr>
                                    <th className={s.th}>Cohort</th>
                                    <th className={s.th}>Dates</th>
                                    <th className={s.th}>Students</th>
                                    <th className={s.th}>Suggestions</th>
                                    <th className={s.th}><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {cohorts.map((c) => (
                                    <tr key={c.CohortID} className={`border-t ${s.row}`}>
                                        <td className={s.td}>
                                            <span className={`font-semibold ${s.text}`}>{c.CohortName}</span>
                                            {c.IsCurrent ? <span className="ml-2"><Pill tone="primary">Running</Pill></span> : null}
                                            {c.Ended ? <span className="ml-2"><Pill>Ended</Pill></span> : null}
                                        </td>
                                        <td className={`${s.td} whitespace-nowrap`}>{dateRange(c.StartDate, c.EndDate)}</td>
                                        <td className={`${s.td} tabular-nums`}>{c.StudentCount}</td>
                                        <td className={s.td}>
                                            <span className="inline-flex flex-wrap gap-1.5">
                                                {c.SentOn ? <Pill tone="success">Sent {formatDate(c.SentOn)}</Pill> : <Pill>Not sent</Pill>}
                                                {c.Customized ? <Pill tone="warning">Own list</Pill> : null}
                                            </span>
                                        </td>
                                        <td className={`${s.td} text-right`}>
                                            <span className="inline-flex flex-wrap justify-end gap-2">
                                                <button type="button" className={s.btnSm} onClick={() => setOpenCohortId(c.CohortID)}>Open</button>
                                                {isOrgAdmin && (
                                                    <>
                                                        <button type="button" className={s.btnSm} onClick={() => setCohortForm(c)} aria-label={`Edit ${c.CohortName}`} title="Edit"><Pencil size={12} /></button>
                                                        <button type="button" className={s.btnSm} onClick={() => archive(c)} aria-label={`Archive ${c.CohortName}`} title="Archive"><Archive size={12} /></button>
                                                    </>
                                                )}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>

            {editingList && (
                <SuggestionEditor
                    title={`Suggestions for ${programName}`}
                    hint="Every cohort sends this list unless it has its own. Changing it doesn't change what students already have."
                    initial={suggestions}
                    allowEmpty
                    onSave={saveProgramList}
                    onClose={() => setEditingList(false)}
                />
            )}
            {cohortForm && (
                <CohortForm orgId={orgId} programId={programId} cohort={cohortForm}
                            onClose={() => setCohortForm(null)}
                            onSaved={async () => { setCohortForm(null); await load(); }} />
            )}
            {openCohortId && (
                <CohortDetail
                    orgId={orgId}
                    cohortId={openCohortId}
                    programName={programName}
                    isOrgAdmin={isOrgAdmin}
                    unassignedCount={unassignedCount}
                    otherCohorts={cohorts.filter((c) => c.CohortID !== openCohortId)}
                    onChanged={load}
                    onClose={() => setOpenCohortId(null)}
                />
            )}
        </>
    );
}

function CohortForm({ orgId, programId, cohort, onClose, onSaved }) {
    const s = useOrgStyles();
    const isNew = !cohort.CohortID;
    const [form, setForm] = useState({
        cohortName: cohort.CohortName || "",
        startDate: cohort.StartDate ? String(cohort.StartDate).slice(0, 10) : "",
        endDate: cohort.EndDate ? String(cohort.EndDate).slice(0, 10) : "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const body = { cohortName: form.cohortName, startDate: form.startDate || null, endDate: form.endDate || null };
            if (isNew) await api.post(`/org/${orgId}/programs/${programId}/cohorts`, body);
            else await api.put(`/org/${orgId}/cohorts/${cohort.CohortID}`, body);
            await onSaved();
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal isOpen onClose={onClose} title={isNew ? "New cohort" : `Edit ${cohort.CohortName}`}
                  footer={<>
                      <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                      <button type="submit" form="cohort-form" className={s.btnPrimary} disabled={saving}>
                          {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Add cohort" : "Save"}
                      </button>
                  </>}>
            <Alert>{error}</Alert>
            <form id="cohort-form" onSubmit={save} className="space-y-4">
                <Field label="Name" htmlFor="cohort-name" hint="For example Fall 2026.">
                    <input id="cohort-name" className={s.input} value={form.cohortName} onChange={set("cohortName")} maxLength={200} required />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Starts" htmlFor="cohort-start">
                        <input id="cohort-start" type="date" className={s.input} value={form.startDate} onChange={set("startDate")} />
                    </Field>
                    <Field label="Ends" htmlFor="cohort-end">
                        <input id="cohort-end" type="date" className={s.input} value={form.endDate} onChange={set("endDate")} />
                    </Field>
                </div>
                <p className={`text-xs ${s.muted}`}>Students who join after the end date don't get this cohort's suggestions.</p>
            </form>
        </OrgModal>
    );
}

function CohortDetail({ orgId, cohortId, programName, isOrgAdmin, unassignedCount, otherCohorts, onChanged, onClose }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/cohorts/${cohortId}`);
            setData(res.data);
        } catch (err) {
            setError(apiError(err, "Couldn't load this cohort."));
        }
    }, [orgId, cohortId]);

    useEffect(() => { load(); }, [load]);

    // Runs a change, then shows the cohort (from the reply, or reloaded) and refreshes the cohort list.
    const act = async (fn, message) => {
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const res = await fn();
            if (res?.data?.cohort) setData(res.data);
            else await load();
            if (message) setNotice(typeof message === "function" ? message(res?.data) : message);
            await onChanged();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <OrgModal isOpen onClose={onClose} title="Cohort" wide>
                <Alert>{error}</Alert>
                {!error && <InlineTableSkeleton isDarkMode={s.dark} rows={3} cols={2} bordered={false} />}
            </OrgModal>
        );
    }

    const { cohort, suggestions, customized, students, canSend } = data;
    const name = `${programName} · ${cohort.CohortName}`;

    // The list editor replaces this window while it's open (two stacked windows would both close on Esc).
    if (editing) {
        const saveOwnList = async (items) => {
            const res = await api.put(`/org/${orgId}/cohorts/${cohortId}/suggestions`, { suggestions: items });
            setData(res.data);
            setEditing(false);
            setNotice("This cohort's list is saved.");
            await onChanged();
        };
        return (
            <SuggestionEditor
                title={`Suggestions for ${name}`}
                hint={customized
                    ? "This cohort's own list. Changing it doesn't change what students already have."
                    : "Saving gives this cohort its own copy of the program's list. Other cohorts keep using the program's list."}
                initial={suggestions}
                onSave={saveOwnList}
                onClose={() => setEditing(false)}
            />
        );
    }

    const send = () => {
        const until = cohort.EndDate ? ` until ${formatDate(cohort.EndDate)}` : "";
        const question = cohort.SentOn
            ? `Send ${cohort.CohortName}'s list again? Students only get the items they don't have yet.`
            : students.length
                ? `Send ${plural(suggestions.length, "suggestion")} to the ${plural(students.length, "student")} in ${cohort.CohortName}? `
                  + `Students who join this cohort later${until} get them too.`
                : `Send ${cohort.CohortName}'s list now? Nobody is in it yet; students who join${until} get it.`;
        if (!window.confirm(question)) return;
        act(() => api.post(`/org/${orgId}/cohorts/${cohortId}/send`),
            (r) => (r?.created ? `Sent ${plural(r.created, "suggestion")}.`
                : r?.students ? "Everyone in this cohort already has these suggestions."
                    : "Marked as sent. Students who join this cohort get the list."));
    };
    const backToProgramList = () => {
        if (!window.confirm(`Use ${programName}'s list for ${cohort.CohortName} again? This cohort's own list goes away. Students keep what they already have.`)) return;
        act(() => api.delete(`/org/${orgId}/cohorts/${cohortId}/suggestions`), "This cohort uses the program's list again.");
    };
    const addUnassigned = () => act(() => api.post(`/org/${orgId}/cohorts/${cohortId}/students`, { unassigned: true }),
        (r) => `Added ${plural(r?.moved || 0, "student")}${r?.created ? ` and sent them ${plural(r.created, "suggestion")}` : ""}.`);
    const moveTo = (student, target) => {
        const to = otherCohorts.find((c) => String(c.CohortID) === String(target));
        if (!to) return;
        act(() => api.post(`/org/${orgId}/cohorts/${to.CohortID}/students`, { clientIds: [student.ClientID] }),
            (r) => `${student.FirstName} ${student.LastName} moved to ${to.CohortName}${r?.created ? ` and got ${plural(r.created, "suggestion")}` : ""}.`);
    };

    const sendLabel = cohort.SentOn ? "Send again" : students.length ? `Send to ${plural(students.length, "student")}` : "Send";
    return (
        <OrgModal isOpen onClose={onClose} title={name} wide
                  footer={<>
                      <button type="button" className={s.btnSecondary} onClick={onClose}>Close</button>
                      {isOrgAdmin && (
                          <button type="button" className={s.btnPrimary} onClick={send}
                                  disabled={busy || !canSend || suggestions.length === 0}>
                              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {sendLabel}
                          </button>
                      )}
                  </>}>
            <div className="space-y-6">
                <div>
                    <Alert onClose={() => setError("")}>{error}</Alert>
                    <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
                    <p className={`text-sm ${s.muted}`}>
                        {dateRange(cohort.StartDate, cohort.EndDate)}
                        {cohort.SentOn
                            ? ` · Sent ${formatDate(cohort.SentOn)}. Students who join later get the list too.`
                            : " · Not sent yet."}
                    </p>
                    {isOrgAdmin && !canSend && (
                        <p className={`mt-3 rounded-lg border px-3 py-2 text-sm ${s.subPanel} ${s.body}`}>{SENDING_OFF}</p>
                    )}
                </div>

                <section>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h4 className={`flex flex-wrap items-center gap-2 text-sm font-bold ${s.text}`}>
                            What this cohort sends
                            <Pill tone={customized ? "warning" : "neutral"}>{customized ? "Its own list" : "The program's list"}</Pill>
                        </h4>
                        {isOrgAdmin && (
                            <span className="flex flex-wrap gap-2">
                                <button type="button" className={s.btnSm} onClick={() => setEditing(true)} disabled={busy}>
                                    <Pencil size={12} /> {customized ? "Edit this cohort's list" : "Change for this cohort"}
                                </button>
                                {customized && <button type="button" className={s.btnSm} onClick={backToProgramList} disabled={busy}>Use the program's list</button>}
                            </span>
                        )}
                    </div>
                    <SuggestionList items={suggestions} empty="Nothing to send yet. Add suggestions to the program's list first." />
                </section>

                <section>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h4 className={`text-sm font-bold ${s.text}`}>Students ({students.length})</h4>
                        {isOrgAdmin && unassignedCount > 0 && (
                            <button type="button" className={s.btnSm} onClick={addUnassigned} disabled={busy}>
                                <Plus size={12} /> Add the {plural(unassignedCount, "student")} with no cohort
                            </button>
                        )}
                    </div>
                    {students.length === 0 ? (
                        <p className={`text-sm ${s.muted}`}>No students yet. Students who join the program while this cohort runs are added automatically.</p>
                    ) : (
                        <div className="relative overflow-x-auto">
                            <table className="w-full min-w-[480px]">
                                <thead>
                                    <tr>
                                        <th className={s.th}>Student</th>
                                        <th className={s.th}>Program suggestions they have</th>
                                        {isOrgAdmin && otherCohorts.length > 0 && <th className={s.th}>Move to</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map((st) => (
                                        <tr key={st.ClientID} className={`border-t ${s.row}`}>
                                            <td className={s.td}>{st.FirstName} {st.LastName}</td>
                                            <td className={`${s.td} tabular-nums`}>{st.Received}</td>
                                            {isOrgAdmin && otherCohorts.length > 0 && (
                                                <td className={s.td}>
                                                    <select className={`${s.input} max-w-[12rem] py-1 text-xs`} value="" disabled={busy}
                                                            aria-label={`Move ${st.FirstName} ${st.LastName} to another cohort`}
                                                            onChange={(e) => moveTo(st, e.target.value)}>
                                                        <option value="">Choose a cohort…</option>
                                                        {otherCohorts.map((c) => <option key={c.CohortID} value={c.CohortID}>{c.CohortName}</option>)}
                                                    </select>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </OrgModal>
    );
}

function SuggestionEditor({ title, hint, initial, allowEmpty = false, onSave, onClose }) {
    const s = useOrgStyles();
    const [rows, setRows] = useState(() => (initial.length ? initial : [{}]).map((it, i) => ({
        key: `r${i}`, title: it.Title || "", description: it.Description || "",
    })));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const update = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
    const move = (i, d) => setRows((r) => {
        const next = [...r];
        [next[i], next[i + d]] = [next[i + d], next[i]];
        return next;
    });
    const remove = (i) => setRows((r) => r.filter((_, j) => j !== i));
    const add = () => setRows((r) => [...r, { key: `n${Date.now()}`, title: "", description: "" }]);

    const save = async (e) => {
        e.preventDefault();
        const items = rows.filter((r) => r.title.trim() || r.description.trim())
            .map((r) => ({ title: r.title.trim(), description: r.description.trim() }));
        if (items.some((it) => !it.title)) { setError("Each suggestion needs a title."); return; }
        if (!items.length && !allowEmpty) { setError("Add at least one suggestion."); return; }
        setSaving(true);
        setError("");
        try {
            await onSave(items);
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal isOpen onClose={onClose} title={title} wide
                  footer={<>
                      <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                      <button type="submit" form="suggestion-editor" className={s.btnPrimary} disabled={saving}>
                          {saving && <Loader2 size={14} className="animate-spin" />} Save
                      </button>
                  </>}>
            <p className={`mb-4 text-sm ${s.muted}`}>{hint}</p>
            <Alert>{error}</Alert>
            <form id="suggestion-editor" onSubmit={save} className="space-y-4">
                <ol className="space-y-3">
                    {rows.map((row, i) => (
                        <li key={row.key} className={`rounded-xl border p-3 ${s.subPanel}`}>
                            <div className="flex items-start gap-2">
                                <span className={`mt-2 w-5 shrink-0 text-right text-sm tabular-nums ${s.muted}`}>{i + 1}.</span>
                                <div className="min-w-0 flex-1 space-y-2">
                                    <input className={s.input} value={row.title} maxLength={255} placeholder="Title, like Update your resume"
                                           aria-label={`Suggestion ${i + 1} title`} onChange={(e) => update(i, "title", e.target.value)} />
                                    <textarea className={s.input} rows={2} value={row.description} maxLength={2000} placeholder="Details (optional)"
                                              aria-label={`Suggestion ${i + 1} details`} onChange={(e) => update(i, "description", e.target.value)} />
                                </div>
                                <div className="flex shrink-0 flex-col gap-1">
                                    <button type="button" className={s.btnSm} onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move suggestion ${i + 1} up`} title="Move up"><ArrowUp size={12} /></button>
                                    <button type="button" className={s.btnSm} onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label={`Move suggestion ${i + 1} down`} title="Move down"><ArrowDown size={12} /></button>
                                    <button type="button" className={s.btnSm} onClick={() => remove(i)} aria-label={`Remove suggestion ${i + 1}`} title="Remove"><Trash2 size={12} /></button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className="flex flex-wrap items-center gap-3">
                    <button type="button" className={s.btnSecondary} onClick={add} disabled={rows.length >= MAX_SUGGESTIONS}>
                        <Plus size={14} /> Add suggestion
                    </button>
                    <span className={`text-xs ${s.muted}`}>Up to {MAX_SUGGESTIONS}. Students see them in this order.</span>
                </div>
            </form>
        </OrgModal>
    );
}
